import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router";

import { Hero } from "@/app/Hero";
import { Shell } from "@/app/Shell";
import { AuthProvider } from "@/auth/AuthProvider";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useBranding } from "@/lib/useBranding";
import { AdminPage } from "@/pages/Admin";
import { BillingPage } from "@/pages/Billing";
import { ChoosePasswordPage } from "@/pages/ChoosePassword";
import { FinishSignInPage } from "@/pages/FinishSignIn";
import { PrivacyPage, TermsPage } from "@/pages/Legal";
import { LoginPage } from "@/pages/Login";
import { ExamplesPage, PublishedExamplePage } from "@/pages/Examples";
import { PricingPage } from "@/pages/Pricing";
import { ResultPage } from "@/pages/Result";
import { StudyHome, StudyRedirect } from "@/pages/StudyHome";
import { ApiError } from "@/lib/queries";
import { TermsGate } from "@/app/TermsGate";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      // A 429 means the organisation is asking too often, and the allowance refills within seconds,
      // so it is worth waiting out rather than showing. Any other 4xx will give the same answer
      // however many times it is asked, so it fails at once instead of being retried into it.
      retry: (count, error) => {
        const status = error instanceof ApiError ? error.status : 0;
        if (status === 429) return count < 3;
        if (status >= 400 && status < 500) return false;
        return count < 1;
      },
      retryDelay: (count, error) =>
        error instanceof ApiError && error.status === 429 ? 3000 * (count + 1) : 1000 * 2 ** count,
    },
  },
});

/** Every view has a URL (decision 4.4): back works, and a Result can be linked to. */
export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      {/* Long enough not to fire at a mouse passing over, short enough to feel like an answer. */}
      <TooltipProvider delay={150}>
        <AuthProvider>
          <BrandingApplier />
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/login/finish" element={<FinishSignInPage />} />
              <Route path="/login/reset" element={<ChoosePasswordPage />} />
              {/* Public: no sign-in, and nothing here can reach a study (app/api/showcase.py). */}
              <Route path="/examples" element={<ExamplesPage />} />
              <Route path="/examples/:slug" element={<PublishedExamplePage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route element={<TermsGate><Shell /></TermsGate>}>
                <Route index element={<Hero />} />
                <Route path="billing" element={<BillingPage />} />
                <Route path="admin" element={<AdminPage />} />
                <Route path="studies/:studyId" element={<StudyHome />} />
                <Route path="studies/:studyId/runs/:runId" element={<ResultPage />} />
                <Route path="studies/:studyId/*" element={<StudyRedirect />} />
              </Route>
            </Routes>
          </BrowserRouter>
          <Toaster theme="dark" position="bottom-right" />
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

/** Applies the brand to the tab title and theme colour as soon as it arrives, on every page. */
function BrandingApplier() {
  useBranding();
  return null;
}
